import React, { useEffect, useMemo } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import {
  VYJEZD_CREW_OPTIONS,
  VYJEZD_STORAGE_TYPES,
  getTransportPlacesByType,
  getTransportPlaceById,
} from "../../data/transportPlaces";
import { INSURANCE_PROVIDERS, getInsuranceProviderByCode } from "../../data/insuranceProviders";
import { VEHICLE_OPTIONS, getVehicleLabelById } from "../../utils/calendarRoles";
import { mergeOrder } from "../../utils/orderDefaults";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  color: "#111827",
  marginBottom: 8,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  fontSize: 15,
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};

const readOnlyStyle = {
  ...inputStyle,
  background: "#f3f4f6",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 120,
  resize: "vertical",
};

const infoBoxStyle = {
  marginBottom: 18,
  padding: 14,
  borderRadius: 16,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
};

function toDatetimeLocalValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes("T")) return raw.slice(0, 16);
  return raw;
}

function getVyjezdPlatceByRezim(rezim) {
  const key = String(rezim || "").trim();
  if (key === "soudni_pitva") return "pcr";
  if (key === "zdravotni_pitva") return "pojistovna";
  return "rodina";
}

function splitDatetimeLocal(value) {
  const raw = String(value || "").trim();
  if (!raw) return { date: "", time: "" };

  if (raw.includes("T")) {
    const [datePart, timePart = ""] = raw.split("T");
    return {
      date: String(datePart || "").slice(0, 10),
      time: String(timePart || "").slice(0, 5),
    };
  }

  if (raw.includes(" ")) {
    const parts = raw.split(" ");
    return {
      date: String(parts[0] || "").slice(0, 10),
      time: String(parts[1] || "").slice(0, 5),
    };
  }

  return { date: raw.slice(0, 10), time: "" };
}

function joinNonEmpty(values = [], sep = " ") {
  return values.map((x) => String(x || "").trim()).filter(Boolean).join(sep);
}

export default function Pohrebka2VyjezdForm() {
  const { order, patchSection, saveOrder, startNewOrder } = usePohrebka2();

  const vyjezd = order?.vyjezd || {};
  const vstupni = order?.vstupniInformace || {};
  const zesnula = order?.zesnulaOsoba || {};

  const storageType = String(vyjezd?.storageType || "").trim();
  const vyjezdRezim = String(vyjezd?.vyjezdRezim || "").trim();
  const vyjezdPlatce = String(vyjezd?.vyjezdPlatce || "").trim();
  const placeOptions = useMemo(
    () => getTransportPlacesByType(storageType),
    [storageType]
  );

  useEffect(() => {
    const patch = {};

    if (!String(vyjezd?.jmenoZesnuleho || "").trim()) {
      patch.jmenoZesnuleho =
        String(
          zesnula?.jmeno ||
          vstupni?.jmenoZesnuleho ||
          ""
        ).trim();
    }

    if (!String(vyjezd?.prijmeniZesnuleho || "").trim()) {
      patch.prijmeniZesnuleho =
        String(
          zesnula?.prijmeni ||
          vstupni?.prijmeniZesnuleho ||
          ""
        ).trim();
    }

    if (!String(vyjezd?.datumNarozeni || "").trim()) {
      patch.datumNarozeni =
        String(
          zesnula?.datumNarozeni ||
          vstupni?.datumNarozeni ||
          ""
        ).trim();
    }

    if (!String(vyjezd?.datumUmrti || "").trim()) {
      patch.datumUmrti =
        String(
          zesnula?.datumUmrti ||
          vstupni?.datumUmrti ||
          ""
        ).trim();
    }

    if (!String(vyjezd?.casUmrti || "").trim()) {
      patch.casUmrti =
        String(
          zesnula?.casUmrti ||
          vstupni?.casUmrti ||
          ""
        ).trim();
    }

    if (!String(vyjezd?.adresaUmrti || "").trim()) {
      patch.adresaUmrti =
        String(
          zesnula?.adresaUmrti ||
          vstupni?.adresaUmrti ||
          ""
        ).trim();
    }

    if (Object.keys(patch).length) {
      patchSection("vyjezd", patch);
    }
  }, [
    vyjezd?.jmenoZesnuleho,
    vyjezd?.prijmeniZesnuleho,
    vyjezd?.datumNarozeni,
    vyjezd?.datumUmrti,
    vyjezd?.casUmrti,
    vyjezd?.adresaUmrti,
    zesnula?.jmeno,
    zesnula?.prijmeni,
    zesnula?.datumNarozeni,
    zesnula?.datumUmrti,
    zesnula?.casUmrti,
    zesnula?.adresaUmrti,
    vstupni?.jmenoZesnuleho,
    vstupni?.prijmeniZesnuleho,
    vstupni?.datumNarozeni,
    vstupni?.datumUmrti,
    vstupni?.casUmrti,
    vstupni?.adresaUmrti,
    patchSection,
  ]);

  const setField = (name, value) => {
    patchSection("vyjezd", { [name]: value });
  };

  const handleStorageTypeChange = (value) => {
    patchSection("vyjezd", {
      storageType: value,
      storagePlaceId: "",
      storagePlaceName: "",
      storageAddress: "",
      storageCity: "",
      storageNote: "",
    });
  };

  const handleStoragePlaceChange = (placeId) => {
    const place = getTransportPlaceById(placeId);

    patchSection("vyjezd", {
      storagePlaceId: placeId,
      storagePlaceName: place?.name || "",
      storageAddress: place?.address || "",
      storageCity: place?.city || "",
      storageNote: place?.note || "",
    });
  };

  const handleVehicleChange = (vehicleId) => {
    patchSection("vyjezd", {
      vehicleId,
      vehicleLabel: getVehicleLabelById(vehicleId) || "",
      vehicleSpz: "",
    });
  };

  const handleVyjezdRezimChange = (value) => {
    const platce = getVyjezdPlatceByRezim(value);

    patchSection("vyjezd", {
      vyjezdRezim: value,
      vyjezdPlatce: platce,
      fakturaceSubjekt:
        platce === "pcr"
          ? "Policie ČR"
          : platce === "pojistovna"
            ? "Zdravotní pojišťovna"
            : "Rodina",
      ...(platce !== "pojistovna"
        ? {
            zdravotniPojistovnaKod: "",
            zdravotniPojistovnaNazev: "",
            zdravotniPojistovnaAdresa: "",
            zdravotniPojistovnaEmail: "",
          }
        : {}),
    });

    patchSection("zaklad", {
      vyjezdRezim: value,
      vyjezdPlatce: platce,
    });
  };

  const handleInsuranceChange = (code) => {
    const item = getInsuranceProviderByCode(code);

    patchSection("vyjezd", {
      zdravotniPojistovnaKod: code,
      zdravotniPojistovnaNazev: item?.name || "",
      zdravotniPojistovnaAdresa: item?.address || "",
      zdravotniPojistovnaEmail: item?.email || "",
      fakturaceSubjekt: item?.shortName || "Zdravotní pojišťovna",
      fakturaceNazev: item?.name || "",
      fakturaceAdresa: item?.address || "",
      fakturaceEmail: item?.email || "",
    });

    patchSection("zaklad", {
      zdravotniPojistovnaKod: code,
      zdravotniPojistovnaNazev: item?.name || "",
    });
  };

  const handleSaveVyjezd = async () => {
    const fullName = joinNonEmpty(
      [vyjezd?.jmenoZesnuleho, vyjezd?.prijmeniZesnuleho],
      " "
    );

    const crewLabel = joinNonEmpty(
      [vyjezd?.crew1, vyjezd?.crew2],
      " + "
    );

    const startInfo = splitDatetimeLocal(vyjezd?.startAt);
    const endInfo = splitDatetimeLocal(vyjezd?.endAt);

    const storageTypeLabel =
      VYJEZD_STORAGE_TYPES.find((x) => String(x?.id || "") === String(vyjezd?.storageType || ""))?.label || "";

    const vyjezdPlatceKey = String(vyjezd?.vyjezdPlatce || "").trim();
    const maNavazujiciRodinnyPrevoz =
      vyjezdPlatceKey === "pcr" || vyjezdPlatceKey === "pojistovna";

    const preparedOrder = mergeOrder(order || {}, {
      zaklad: {
        typZakazky: String((order?.zaklad?.typZakazky || "vyjezd")).trim(),
        stavZakazky: String(order?.zaklad?.stavZakazky || "rozpracovano").trim(),
        datumSjednani:
          String(order?.zaklad?.datumSjednani || "").trim() ||
          startInfo.date ||
          String(vyjezd?.datumUmrti || "").trim(),
        zesnuly:
          String(order?.zaklad?.zesnuly || "").trim() ||
          fullName,
        vyjezdRezim: String(vyjezd?.vyjezdRezim || "").trim(),
        vyjezdPlatce: String(vyjezd?.vyjezdPlatce || "").trim(),
        zdravotniPojistovnaKod: String(vyjezd?.zdravotniPojistovnaKod || "").trim(),
        zdravotniPojistovnaNazev: String(vyjezd?.zdravotniPojistovnaNazev || "").trim(),
      },

      vyjezd: {
        ...vyjezd,
        externiFakturace: {
          ...((vyjezd?.externiFakturace) || {}),
          pcr: {
            ...(((vyjezd?.externiFakturace) || {}).pcr || {}),
            aktivni: String(vyjezd?.vyjezdPlatce || "").trim() === "pcr",
            datum:
              ((((vyjezd?.externiFakturace) || {}).pcr || {}).datum) ||
              startInfo.date ||
              "",
            subjekt: "Policie ČR",
            adresa:
              ((((vyjezd?.externiFakturace) || {}).pcr || {}).adresa) ||
              String(vyjezd?.fakturaceAdresa || "").trim(),
            email:
              ((((vyjezd?.externiFakturace) || {}).pcr || {}).email) ||
              String(vyjezd?.fakturaceEmail || "").trim(),
          },
          pojistovna: {
            ...(((vyjezd?.externiFakturace) || {}).pojistovna || {}),
            aktivni: String(vyjezd?.vyjezdPlatce || "").trim() === "pojistovna",
            datum:
              ((((vyjezd?.externiFakturace) || {}).pojistovna || {}).datum) ||
              startInfo.date ||
              "",
            subjekt:
              ((((vyjezd?.externiFakturace) || {}).pojistovna || {}).subjekt) ||
              String(vyjezd?.zdravotniPojistovnaNazev || "").trim(),
            adresa:
              ((((vyjezd?.externiFakturace) || {}).pojistovna || {}).adresa) ||
              String(vyjezd?.zdravotniPojistovnaAdresa || "").trim(),
            email:
              ((((vyjezd?.externiFakturace) || {}).pojistovna || {}).email) ||
              String(vyjezd?.zdravotniPojistovnaEmail || "").trim(),
          },
        },
      },

      prevoz: {
        ...((order?.prevoz) || {}),
        mistoOdvozu:
          String(order?.prevoz?.mistoOdvozu || "").trim() ||
          "Místo úmrtí",
        adresaOdvozu:
          String(order?.prevoz?.adresaOdvozu || "").trim() ||
          String(vyjezd?.adresaUmrti || "").trim(),
        cilPrevozuTyp:
          String(order?.prevoz?.cilPrevozuTyp || "").trim() ||
          "pitva_patologie",
        cilPrevozuNazev:
          String(order?.prevoz?.cilPrevozuNazev || "").trim() ||
          String(vyjezd?.storagePlaceName || "").trim(),
        cilPrevozuAdresa:
          String(order?.prevoz?.cilPrevozuAdresa || "").trim() ||
          String(vyjezd?.storageAddress || "").trim(),
        datumPrevozu:
          String(order?.prevoz?.datumPrevozu || "").trim() ||
          startInfo.date,
        casPrevozu:
          String(order?.prevoz?.casPrevozu || "").trim() ||
          startInfo.time,
        vozidlo:
          String(order?.prevoz?.vozidlo || "").trim() ||
          String(vyjezd?.vehicleLabel || vyjezd?.vehicleId || "").trim(),
        navazujiciRodinnyPrevoz:
          typeof order?.prevoz?.navazujiciRodinnyPrevoz === "boolean"
            ? order.prevoz.navazujiciRodinnyPrevoz
            : maNavazujiciRodinnyPrevoz,
        pitvaUzavrena:
          typeof order?.prevoz?.pitvaUzavrena === "boolean"
            ? order.prevoz.pitvaUzavrena
            : false,

        kmRozpad: {
          ...((order?.prevoz?.kmRozpad) || {}),
        },

        fakturaceRozpad: {
          ...((order?.prevoz?.fakturaceRozpad) || {}),
          pcr: {
            ...(((order?.prevoz?.fakturaceRozpad) || {}).pcr || {}),
            subjekt:
              String((((order?.prevoz?.fakturaceRozpad) || {}).pcr || {}).subjekt || "").trim() ||
              "Policie ČR",
            aktivni:
              typeof ((((order?.prevoz?.fakturaceRozpad) || {}).pcr || {}).aktivni) === "boolean"
                ? ((((order?.prevoz?.fakturaceRozpad) || {}).pcr || {}).aktivni)
                : (vyjezdPlatceKey === "pcr"),
          },
          pojistovna: {
            ...(((order?.prevoz?.fakturaceRozpad) || {}).pojistovna || {}),
            subjekt:
              String((((order?.prevoz?.fakturaceRozpad) || {}).pojistovna || {}).subjekt || "").trim() ||
              String(vyjezd?.zdravotniPojistovnaNazev || "").trim(),
            aktivni:
              typeof ((((order?.prevoz?.fakturaceRozpad) || {}).pojistovna || {}).aktivni) === "boolean"
                ? ((((order?.prevoz?.fakturaceRozpad) || {}).pojistovna || {}).aktivni)
                : (vyjezdPlatceKey === "pojistovna"),
          },
          rodina: {
            ...(((order?.prevoz?.fakturaceRozpad) || {}).rodina || {}),
            subjekt:
              String((((order?.prevoz?.fakturaceRozpad) || {}).rodina || {}).subjekt || "").trim() ||
              "Rodina",
            aktivni:
              typeof ((((order?.prevoz?.fakturaceRozpad) || {}).rodina || {}).aktivni) === "boolean"
                ? ((((order?.prevoz?.fakturaceRozpad) || {}).rodina || {}).aktivni)
                : (vyjezdPlatceKey === "rodina" || maNavazujiciRodinnyPrevoz),
          },
        },

        externiFakturace: {
          ...((order?.prevoz?.externiFakturace) || {}),
          pcr: {
            ...(((order?.prevoz?.externiFakturace) || {}).pcr || {}),
            aktivni:
              typeof ((((order?.prevoz?.externiFakturace) || {}).pcr || {}).aktivni) === "boolean"
                ? ((((order?.prevoz?.externiFakturace) || {}).pcr || {}).aktivni)
                : (vyjezdPlatceKey === "pcr"),
            datum:
              String((((order?.prevoz?.externiFakturace) || {}).pcr || {}).datum || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pcr || {}).datum || "").trim() ||
              startInfo.date,
            subjekt:
              String((((order?.prevoz?.externiFakturace) || {}).pcr || {}).subjekt || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pcr || {}).subjekt || "").trim() ||
              "Policie ČR",
            adresa:
              String((((order?.prevoz?.externiFakturace) || {}).pcr || {}).adresa || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pcr || {}).adresa || "").trim() ||
              String(vyjezd?.fakturaceAdresa || "").trim(),
            email:
              String((((order?.prevoz?.externiFakturace) || {}).pcr || {}).email || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pcr || {}).email || "").trim() ||
              String(vyjezd?.fakturaceEmail || "").trim(),
          },
          pojistovna: {
            ...(((order?.prevoz?.externiFakturace) || {}).pojistovna || {}),
            aktivni:
              typeof ((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).aktivni) === "boolean"
                ? ((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).aktivni)
                : (vyjezdPlatceKey === "pojistovna"),
            datum:
              String((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).datum || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pojistovna || {}).datum || "").trim() ||
              startInfo.date,
            subjekt:
              String((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).subjekt || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pojistovna || {}).subjekt || "").trim() ||
              String(vyjezd?.zdravotniPojistovnaNazev || "").trim(),
            adresa:
              String((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).adresa || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pojistovna || {}).adresa || "").trim() ||
              String(vyjezd?.zdravotniPojistovnaAdresa || "").trim(),
            email:
              String((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).email || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pojistovna || {}).email || "").trim() ||
              String(vyjezd?.zdravotniPojistovnaEmail || "").trim(),
            koeficient:
              String((((order?.prevoz?.externiFakturace) || {}).pojistovna || {}).koeficient || "").trim() ||
              String((((vyjezd?.externiFakturace) || {}).pojistovna || {}).koeficient || "").trim(),
          },
        },

        rodinnaFakturace: {
          ...((order?.prevoz?.rodinnaFakturace) || {}),
          subjekt:
            String((order?.prevoz?.rodinnaFakturace || {}).subjekt || "").trim() ||
            "Rodina",
          datum:
            String((order?.prevoz?.rodinnaFakturace || {}).datum || "").trim() ||
            startInfo.date,
        },
      },
    });

    try {
      await saveOrder(preparedOrder);
      startNewOrder();
      window.alert("Výjezd uložen do knihy. Po otevření zakázky z knihy se údaje automaticky doplní do ostatních záložek.");
    } catch (e) {
      window.alert("Uložení výjezdu se nepodařilo: " + String(e?.message || e));
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#111827", marginBottom: 8 }}>
        Výjezd
      </div>

      <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 22 }}>
        Evidence výjezdu, uložení zesnulého, posádky a návaznosti na další část aplikace.
      </div>

      <div style={infoBoxStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
          Automatické předvyplnění
        </div>
        <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.5 }}>
          Jméno, příjmení, datum narození, datum úmrtí, čas úmrtí a adresa úmrtí se sem
          automaticky přenášejí z údajů o zesnulém a vstupních informací. Po uložení se
          výjezd zapíše do knihy a formulář se vyčistí pro další nový výjezd.
        </div>
      </div>

      {vyjezdRezim === "soudni_pitva" ? (
        <div style={{
          marginBottom: 18,
          padding: 14,
          borderRadius: 16,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          fontWeight: 700,
        }}>
          Soudní pitva: výjezd se připravuje pro fakturaci Policii ČR.
        </div>
      ) : null}

      {vyjezdRezim === "zdravotni_pitva" ? (
        <div style={{
          marginBottom: 18,
          padding: 14,
          borderRadius: 16,
          background: "#ecfeff",
          border: "1px solid #a5f3fc",
          color: "#155e75",
          fontWeight: 700,
        }}>
          Zdravotní pitva: pojišťovna hradí jen naložené úseky.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
        <button
          type="button"
          onClick={handleSaveVyjezd}
          style={{
            padding: "14px 18px",
            borderRadius: 14,
            border: "1px solid #111827",
            background: "#111827",
            color: "#fff",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Uložit výjezd
        </button>
      </div>

      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>Režim výjezdu</label>
          <select
            style={inputStyle}
            value={vyjezdRezim}
            onChange={(e) => handleVyjezdRezimChange(e.target.value)}
          >
            <option value="">Vyber režim</option>
            <option value="standard">Standard / rodina</option>
            <option value="soudni_pitva">Soudní pitva</option>
            <option value="zdravotni_pitva">Zdravotní pitva</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Plátce výjezdu</label>
          <input
            style={readOnlyStyle}
            value={
              vyjezdPlatce === "pcr"
                ? "Policie ČR"
                : vyjezdPlatce === "pojistovna"
                  ? "Zdravotní pojišťovna"
                  : vyjezdPlatce === "rodina"
                    ? "Rodina"
                    : ""
            }
            readOnly
          />
        </div>

        <div>
          <label style={labelStyle}>Kód zdravotní pojišťovny</label>
          <select
            style={inputStyle}
            value={vyjezd.zdravotniPojistovnaKod || ""}
            onChange={(e) => handleInsuranceChange(e.target.value)}
            disabled={vyjezdPlatce !== "pojistovna"}
          >
            <option value="">Vyber pojišťovnu</option>
            {INSURANCE_PROVIDERS.map((item) => (
              <option key={item.code} value={item.code}>
                {item.code} — {item.shortName} — {item.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Název pojišťovny</label>
          <input
            style={readOnlyStyle}
            value={vyjezd.zdravotniPojistovnaNazev || ""}
            readOnly
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Fakturační subjekt</label>
          <input
            style={inputStyle}
            value={vyjezd.fakturaceNazev || ""}
            onChange={(e) => setField("fakturaceNazev", e.target.value)}
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Fakturační adresa</label>
          <input
            style={inputStyle}
            value={vyjezd.fakturaceAdresa || ""}
            onChange={(e) => setField("fakturaceAdresa", e.target.value)}
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Fakturační e-mail</label>
          <input
            style={inputStyle}
            value={vyjezd.fakturaceEmail || ""}
            onChange={(e) => setField("fakturaceEmail", e.target.value)}
          />
        </div>

        {vyjezdPlatce === "pcr" ? (
          <div>
            <label style={labelStyle}>Číslo faktury PČR</label>
            <input
              style={inputStyle}
              value={vyjezd?.externiFakturace?.pcr?.cislo || ""}
              onChange={(e) =>
                patchSection("vyjezd", {
                  externiFakturace: {
                    ...(vyjezd?.externiFakturace || {}),
                    pcr: {
                      ...((vyjezd?.externiFakturace || {}).pcr || {}),
                      cislo: e.target.value,
                    },
                  },
                })
              }
            />
          </div>
        ) : null}

        {vyjezdPlatce === "pojistovna" ? (
          <div>
            <label style={labelStyle}>Číslo faktury pojišťovny</label>
            <input
              style={inputStyle}
              value={vyjezd?.externiFakturace?.pojistovna?.cislo || ""}
              onChange={(e) =>
                patchSection("vyjezd", {
                  externiFakturace: {
                    ...(vyjezd?.externiFakturace || {}),
                    pojistovna: {
                      ...((vyjezd?.externiFakturace || {}).pojistovna || {}),
                      cislo: e.target.value,
                    },
                  },
                })
              }
            />
          </div>
        ) : null}
        <div>
          <label style={labelStyle}>Jméno zesnulého</label>
          <input
            style={inputStyle}
            value={vyjezd.jmenoZesnuleho || ""}
            onChange={(e) => setField("jmenoZesnuleho", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Příjmení zesnulého</label>
          <input
            style={inputStyle}
            value={vyjezd.prijmeniZesnuleho || ""}
            onChange={(e) => setField("prijmeniZesnuleho", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Datum narození</label>
          <input
            type="date"
            style={inputStyle}
            value={vyjezd.datumNarozeni || ""}
            onChange={(e) => setField("datumNarozeni", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Datum úmrtí</label>
          <input
            type="date"
            style={inputStyle}
            value={vyjezd.datumUmrti || ""}
            onChange={(e) => setField("datumUmrti", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Čas úmrtí</label>
          <input
            type="time"
            style={inputStyle}
            value={vyjezd.casUmrti || ""}
            onChange={(e) => setField("casUmrti", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Adresa úmrtí</label>
          <input
            style={inputStyle}
            value={vyjezd.adresaUmrti || ""}
            onChange={(e) => setField("adresaUmrti", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Fotka ohledacího listu</label>
          <input
            style={inputStyle}
            placeholder="URL / cesta / poznámka"
            value={vyjezd.fotkaOhledacihoListu || ""}
            onChange={(e) => setField("fotkaOhledacihoListu", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Fotka zesnulého</label>
          <input
            style={inputStyle}
            placeholder="URL / cesta / poznámka"
            value={vyjezd.fotkaZesnuleho || ""}
            onChange={(e) => setField("fotkaZesnuleho", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Typ uložení</label>
          <select
            style={inputStyle}
            value={storageType}
            onChange={(e) => handleStorageTypeChange(e.target.value)}
          >
            <option value="">Vyber typ uložení</option>
            {VYJEZD_STORAGE_TYPES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Místo uložení</label>
          <select
            style={inputStyle}
            value={vyjezd.storagePlaceId || ""}
            onChange={(e) => handleStoragePlaceChange(e.target.value)}
          >
            <option value="">Vyber místo</option>
            {placeOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Adresa patologie / uložení</label>
          <input
            style={readOnlyStyle}
            value={vyjezd.storageAddress || ""}
            readOnly
          />
        </div>

        <div>
          <label style={labelStyle}>Město</label>
          <input
            style={readOnlyStyle}
            value={vyjezd.storageCity || ""}
            readOnly
          />
        </div>

        <div>
          <label style={labelStyle}>Poznámka k místu</label>
          <input
            style={readOnlyStyle}
            value={vyjezd.storageNote || ""}
            readOnly
          />
        </div>

        <div>
          <label style={labelStyle}>Posádka 1</label>
          <select
            style={inputStyle}
            value={vyjezd.crew1 || ""}
            onChange={(e) => setField("crew1", e.target.value)}
          >
            <option value="">Vyber pracovníka</option>
            {VYJEZD_CREW_OPTIONS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Posádka 2</label>
          <select
            style={inputStyle}
            value={vyjezd.crew2 || ""}
            onChange={(e) => setField("crew2", e.target.value)}
          >
            <option value="">Vyber pracovníka</option>
            {VYJEZD_CREW_OPTIONS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Od kdy</label>
          <input
            type="datetime-local"
            style={inputStyle}
            value={toDatetimeLocalValue(vyjezd.startAt)}
            onChange={(e) => setField("startAt", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Do kdy</label>
          <input
            type="datetime-local"
            style={inputStyle}
            value={toDatetimeLocalValue(vyjezd.endAt)}
            onChange={(e) => setField("endAt", e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Vozidlo</label>
          <select
            style={inputStyle}
            value={vyjezd.vehicleId || ""}
            onChange={(e) => handleVehicleChange(e.target.value)}
          >
            <option value="">Vyber vozidlo</option>
            {(Array.isArray(VEHICLE_OPTIONS) ? VEHICLE_OPTIONS : []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Název vozidla</label>
          <input
            style={readOnlyStyle}
            value={vyjezd.vehicleLabel || ""}
            readOnly
          />
        </div>

        <div>
          <label style={labelStyle}>SPZ</label>
          <input
            style={inputStyle}
            value={vyjezd.vehicleSpz || ""}
            onChange={(e) => setField("vehicleSpz", e.target.value)}
            placeholder="SPZ"
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={labelStyle}>Poznámka k výjezdu</label>
        <textarea
          style={textareaStyle}
          value={vyjezd.poznamka || ""}
          onChange={(e) => setField("poznamka", e.target.value)}
        />
      </div>
    </div>
  );
}
